<template>
    <div class="lib-row" :style="$listeners.click ? 'cursor: pointer' : null" @click="$emit('click')">
        <habit-icon combo :size="42" />
        <div class="lib-info">
            <div class="lib-name text-truncate">{{ combo.name }}</div>
            <div class="lib-meta text-truncate">{{ meta }}</div>
            <div class="hab-mini">
                <button-tooltip v-for="habit in combo.habits" :key="habit.key" :tooltip="habitName(habit)" top>
                    <template v-slot:activator="{ on, attrs }">
                        <span class="hab-mini-ic" v-bind="attrs" v-on="on">
                            <v-icon size="12">{{ categoryIcon(habit.category) }}</v-icon>
                        </span>
                    </template>
                </button-tooltip>
            </div>
        </div>
        <button-tooltip v-if="adherence !== null" :tooltip="$t('habits.adherence_tooltip')" top>
            <template v-slot:activator="{ on, attrs }">
                <span class="hab-adh" :class="`hab-adh--${adherenceLevel(adherence)}`" v-bind="attrs" v-on="on">{{ adherence }}%</span>
            </template>
        </button-tooltip>
        <div v-if="badge" class="lib-badge">{{ badge }}</div>
        <v-menu offset-y v-if="withMenu">
            <template v-slot:activator="{ on, attrs }">
                <button type="button" class="lib-more" v-bind="attrs" v-on="on" @click.stop @mousedown.stop>
                    <v-icon size="18">mdi-dots-vertical</v-icon>
                </button>
            </template>
            <v-list dense>
                <v-list-item link @click="$emit('click:edit')">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-pencil-outline</v-icon>
                    <v-list-item-title>{{ $t('common.edit') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="$emit('click:copy')">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                    <v-list-item-title>{{ $t('common.duplicate') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="$emit('click:assign')">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-account-arrow-right-outline</v-icon>
                    <v-list-item-title>{{ $t('common.assign') }}</v-list-item-title>
                </v-list-item>
                <v-list-item link @click="$emit('click:folder')">
                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-folder-outline</v-icon>
                    <v-list-item-title>{{ $t('common.save_in_folder') }}</v-list-item-title>
                </v-list-item>
                <v-divider />
                <v-list-item link @click="$emit('click:delete')">
                    <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                    <v-list-item-title class="error--text">{{ $t('habits.delete_combo') }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import HabitIcon from './HabitIcon.vue'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import habitsMixin from '@/mixins/habitsMixin'
import { HABIT_CATEGORY_ICONS, adherenceLevel } from '@/utils/Habits'

export default {
    name: 'HabitComboCard',
    mixins: [habitsMixin],
    components: { HabitIcon, ButtonTooltip },
    props: {
        combo: {
            type: Object,
            required: true,
        },
        withMenu: {
            type: Boolean,
            default: false,
        },
        badge: {
            type: [Number, String],
            default: null,
        },
    },
    computed: {
        adherence(vm = this) {
            const value = vm.combo.stats?.adherence
            return value === undefined || value === null ? null : value
        },
        meta(vm = this) {
            const parts = [vm.habitFrequencySummary(vm.combo.habits)]
            if (vm.combo.duration) parts.push(vm.$tc('habits.weeks', vm.combo.duration, { n: vm.combo.duration }))
            parts.push(vm.$tc('habits.assigned_count', vm.combo.used || 0, { n: vm.combo.used || 0 }))
            if (vm.combo.alarms?.mode === 'daily') parts.push(vm.$t('habits.one_alarm_day'))
            return parts.filter(Boolean).join(' · ')
        },
    },
    methods: {
        adherenceLevel,
        categoryIcon(category) {
            return HABIT_CATEGORY_ICONS[category] || HABIT_CATEGORY_ICONS.custom
        },
    },
}
</script>

<style scoped>
.lib-row {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all .15s;
}

.lib-row:hover {
    border-color: var(--v-outline2-base);
    background: var(--v-surface2-base);
}

.lib-info {
    flex: 1;
    min-width: 0;
}

.lib-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.lib-meta {
    font-size: 11px;
    color: var(--v-txtSecondary-base);
    margin-top: 2px;
}

.hab-mini {
    display: flex;
    gap: 4px;
    margin-top: 6px;
}

.hab-mini-ic {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.hab-mini-ic .v-icon {
    color: var(--v-accent-base) !important;
}

.hab-adh {
    min-width: 42px;
    height: 24px;
    padding: 0 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.hab-adh--good {
    background: rgba(0, 153, 102, .15);
    color: #009966;
}

.hab-adh--mid {
    background: rgba(232, 192, 0, .15);
    color: #E8C000;
}

.theme--light .hab-adh--mid {
    color: #C8A000;
}

.hab-adh--bad {
    background: rgba(232, 92, 92, .15);
    color: #E85C5C;
}

.lib-badge {
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    border-radius: 12px;
    background: rgba(51, 153, 153, .15);
    color: var(--v-accent-base);
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.lib-more {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    transition: all .15s;
}

.lib-more:hover {
    color: var(--v-txtSecondary-base);
    border-color: var(--v-outline2-base);
}

.lib-more .v-icon {
    color: inherit !important;
}
</style>
